import React, { useEffect, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { borderStyle, cardStyle, contentBoxStyle } from "../shared/Theme";
import RiskOverviewGraph from "../elements/CyberRisk/RiskOverviewGraph";
import Sidebar from "../elements/Sidebar";
import Header from "../elements/Header";
import ragService from "../services/ragService";
import companyService from "../services/companyService";
import { useNavigate } from "react-router-dom";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import CyberRiskRecommendedModal from "../elements/Dashboard/CyberRiskRecommendedModal";
import DashboardDomainServicesMap from "../elements/Dashboard/DashboardDomainServicesMap";
import DomainsListModal from "../elements/CyberRisk/DomainsListModal";
import domainService from "../services/domainService";
import { FiberManualRecord } from "@mui/icons-material";
import DomainInfoModal from "../elements/CyberRisk/DomainInfoModal";
import SyntheticIDModal from "../elements/CyberRisk/SyntheticIDModal";
import ServiceLocationGraph from "../elements/CyberRisk/ServiceLocationGraph";
import ServiceTypesGraph from "../elements/CyberRisk/ServiceTypesGraph";
import AccountGuard from "../elements/Admin/AccountGuard";
import darkWebService from "../services/darkWebService";

const CyberRisk = () => {
	
	const navigate = useNavigate();
	
	let [ riskCounts, setRiskCounts ] = useState(null);
	let [ showRecommendation, setShowRecommendation ] = useState(null);
	let [ showDomains, setShowDomains ] = useState(false);
	let [ showIndividualDomain, setShowIndividualDomain ] = useState(null);
	let [ showSyntheticID, setShowSyntheticID ] = useState(false);
	let [ domains, setDomains ] = useState([]);
	
	const graphSize = "9em";
	
	const handleTitleClick = () => {
		navigate("/risk");
	};
	
	useEffect(() => {
		companyService.onSelectCompany(() => {
			setRiskCounts(ragService.ragCounts());
		});
		
		setRiskCounts(ragService.ragCounts());
		setDomains(domainService.VisibleDomains());
	}, []);
	
	const handleRecommendationClick = (recommendation) => {
		setShowRecommendation(recommendation);
	};
	
	const handleRecommendationModalClose = () => {
		setShowRecommendation(null);
	};
	
	const handleGraphClick = () => {
		navigate("/rags");
	};
	
	const handleDomainsChanged = async () => {
		await ragService.all(companyService.selectedCompany.id);
		await darkWebService.all(companyService.selectedCompany.id);
		setRiskCounts(ragService.ragCounts());
		setDomains(domainService.VisibleDomains());
	}
	
	const handleDomainsClick = () => {
		setShowDomains(true);
	};
	
	const handleDomainsClose = () => {
		setShowDomains(false);
	};

	const handleIndividualDomainClick = (domain) => {
		setShowIndividualDomain(domain);
	};
	
	const handleIndividualDomainClose = () => {
		setShowIndividualDomain(null);
	};

	const handleSyntheticIDClick = () => {
		setShowSyntheticID(true);
	};
	
	const handleSyntheticIDClose = () => {
		setShowSyntheticID(false);
	};
	
	return (
		<Box sx={{ display: "flex" }}>
			<Sidebar/>
			<AccountGuard requiredService="kynd"/>
			<Box component="main" sx={{flexGrow: 1, bgcolor: "background.default"}}>
				<Header pageTitle="Cyber Risk"/>
				<Box sx={contentBoxStyle}>
					<Box sx={{
						width: "80%",
						cursor: "pointer",
						borderBottom: "solid 2px",
						margin: "auto",
						...borderStyle
					}}>
						<Stack direction="row" sx={{ width: "100%" }}>
							<Box sx={{ width: "50%", borderRight: "solid 2px", ...borderStyle }}>
								<Stack direction="column" sx={{ ...cardStyle }}>
									<Typography
										variant="h5"
										sx={{
											width: "100%",
											textAlign: "center",
											marginTop: "1em",
											marginBottom: "0.65em",
											cursor: "pointer",
											fontWeight: "bold"
										}}
										onClick={handleTitleClick}
									>
										Current Cyber Risk
									</Typography>
									{riskCounts?
										<Stack
											direction="row"
											spacing={4}
											justifyContent="center"
											sx={{ marginBottom: "2em" }}
											onClick={handleGraphClick}
										>
											<RiskOverviewGraph
												value={riskCounts.red}
												total={50}
												size={graphSize}
												link="/risk"
												colorOverride="red"
											/>
											<RiskOverviewGraph
												value={riskCounts.amber}
												total={50}
												size={graphSize}
												link="/risk"
												colorOverride="amber"
											/>
										</Stack>
									: <React.Fragment/> }
								</Stack>
							</Box>
							<Box sx={{ width: "50%", ...borderStyle }}>
								<Stack direction="column" sx={{ ...cardStyle }}>
									<Typography
										variant="h5"
										sx={{
											width: "100%",
											textAlign: "center",
											marginTop: "1em",
											marginBottom: "1em",
											cursor: "pointer",
											fontWeight: "bold"
										}}
										onClick={handleTitleClick}
									>
										Top 3 Critical Risks
									</Typography>
									{ragService.recommendations.map((recommendation, i) => {
										if (i >= 3) return ( <React.Fragment key={`top_critical_${i}`}/> );
										return (
											<Stack
												key={`top_critical_${i}`}
												direction="row"
												alignItems="center"
												justifyContent="center"
												onClick={() => {handleRecommendationClick(recommendation)}}
											>
												<Typography variant="h5" sx={{ width: "1em", fontWeight: "bold" }}>
													{`${i + 1}`}
												</Typography>
												<Typography sx={{ width: "20em", fontSize: "1.1em" }}>
													{`${recommendation.title}`}
												</Typography>
												<ArrowBackIosNewIcon sx={{ transform: "rotate(270deg)", color: "#ff03ed" }}/>
											</Stack>
										);
									})}
								</Stack>
							</Box>
						</Stack>
						<CyberRiskRecommendedModal
							recommendation={showRecommendation}
							onClose={handleRecommendationModalClose}
						/>
					</Box>
					<Box sx={{
						width: "80%",
						borderBottom: "solid 2px",
						margin: "auto",
						...borderStyle
					}}>
						<Box sx={{ ...cardStyle }}>
							<Stack direction="row" sx={{ width: "100%" }}>
								<Stack
									direction="column"
									sx={{ width: "30%", margin: "auto" }}
								>
									<Typography
										variant="h5"
										sx={{
											width: "100%",
											textAlign: "center",
											marginTop: "0.5em",
											marginBottom: "0.65em",
											fontWeight: "bold"
										}}
									>
										Service Locations
									</Typography>
									<Typography
										sx={{
											width: "100%",
											textAlign: "center",
											marginTop: "0.5em",
											marginBottom: "1em",
											fontSize: "0.9em"
										}}
									>
										We have identified the locations your internet services are
										running from so you can check there are no surprises
									</Typography>
									<Box sx={{ wdith: "100%", height: "6em" }}>
										<ServiceLocationGraph/>
									</Box>
								</Stack>
								<Box sx={{ minWidth: "38%", margin: "auto" }}>
									<DashboardDomainServicesMap/>
								</Box>
								<Stack
									direction="column"
									sx={{ width: "30%", margin: "auto" }}
								>
									<Typography
										variant="h5"
										sx={{
											width: "100%",
											textAlign: "center",
											marginTop: "0.5em",
											marginBottom: "0.65em",
											fontWeight: "bold"
										}}
									>
										Visible Services
									</Typography>
									<Typography
										sx={{
											width: "100%",
											textAlign: "center",
											marginTop: "0.5em",
											marginBottom: "1em",
											fontSize: "0.9em"
										}}
									>
										These are your visible, internet facing services that we are 
										able to see your business is running<br/>online
									</Typography>
									<Box sx={{ wdith: "100%", height: "6em" }}>
										<ServiceTypesGraph/>
									</Box>
								</Stack>
							</Stack>
						</Box>
					</Box>
					<Box sx={{
						width: "80%",
						margin: "0 auto 2em"
					}}>
						<Stack direction="row" sx={{ width: "100%" }}>
							<Box sx={{ width: "50%", borderRight: "solid 2px", ...borderStyle }}>
								<Stack
									direction="column"
									sx={{ ...cardStyle, paddingBottom: "1em" }}
								>
									<Typography
										variant="h5"
										sx={{
											width: "100%",
											textAlign: "center",
											marginTop: "1em",
											marginBottom: "0.5em",
											fontWeight: "bold"
										}}
									>
										Domain Discovery
									</Typography>
									<Typography
										sx={{
											width: "100%",
											textAlign: "center",
											marginTop: "0.25em",
											marginBottom: "1em",
										}}
									>
										These are the domains that have been associated with your<br/>
										organisation. Click the dropdown for further details or the button below<br/>
										for a full list.
									</Typography>
									<Stack direction="row">
										<Box
											justifyContent="center"
											sx={{
												width: "35%",
												height: "4em",
												border: 2,
												margin: "0.25em auto",
												borderRadius: "2em",
												borderColor: "#ff03ed",
												textAlign: "center",
												padding: "0.5em",
												"&:hover": { cursor: "pointer" }
											}}
											onClick={handleDomainsClick}
										>
											<Typography sx={{ marginTop: "0.65em" }}>
												Full List of Domains
											</Typography>
										</Box>
										<Stack direction="column" sx={{ width: "50%", marginBottom: "0.5em" }}>
											{domains.map((domain, i) => {
												if (i >= 3) return ( <React.Fragment key={`domain_list_item_${i}`}/> );
												return (
													<Stack
														key={`domain_list_item_${i}`}
														direction="row"
														alignItems="center"
														justifyContent="left"
														sx={{ "&:hover": { cursor: "pointer" }, margin: "0" }}													onClick={() => {handleIndividualDomainClick(domain)}}
													>
														<FiberManualRecord sx={{ width: "0.3em", height: "0.3em" }}/>
														<Typography sx={{ width: "60%", margin: "0.25em" }}>
															{`${domain.address}`}
														</Typography>
														<ArrowBackIosNewIcon
															sx={{ transform: "rotate(270deg)", color: "#ff03ed" }}
														/>
													</Stack>
												);
											})}
										</Stack>
									</Stack>
								</Stack>
							</Box>
							<Box sx={{ width: "50%", ...borderStyle }}>
								<Stack direction="column" sx={{ ...cardStyle }}>
									<Typography
										variant="h5"
										sx={{
											width: "100%",
											textAlign: "center",
											marginTop: "1em",
											marginBottom: "0.5em",
											fontWeight: "bold"
										}}
										onClick={handleTitleClick}
									>
										Data Breach Risk
									</Typography>
									<Typography
										sx={{
											width: "100%",
											textAlign: "center",
											marginTop: "0.25em",
											marginBottom: "1em",
										}}
									>
										Be alerted to any unauthorised access or breach of your sensitive<br/>
										databases, helping you respond quicker to incidents and comply<br/>
										with standards like GDPR, CPPA etc.
									</Typography>
									<Box
										justifyContent="center"
										sx={{
											width: "35%",
											height: "4em",
											border: 2,
											margin: "0.25em auto",
											borderRadius: "2em",
											borderColor: "#ff03ed",
											textAlign: "center",
											padding: "0.5em",
											"&:hover": { cursor: "pointer" }
										}}
										onClick={handleSyntheticIDClick}
									>
										<Typography sx={{ marginTop: "0.65em" }}>
											Synthetic ID's
										</Typography>
									</Box>
								</Stack>
							</Box>
						</Stack>
					</Box>
				</Box>
				<DomainsListModal
					open={showDomains}
					onClose={handleDomainsClose}
					onDomainsChanged={handleDomainsChanged}
				/>
				<DomainInfoModal
					domain={showIndividualDomain}
					onClose={handleIndividualDomainClose}
				/>
				<SyntheticIDModal
					open={showSyntheticID}
					onClose={handleSyntheticIDClose}
				/>
			</Box>
		</Box>
	);
}

export default CyberRisk;
